import { Box, Stack, Alert } from '@mui/material';
import {
  FieldMediaUpload,
  FieldSwitch,
  FieldText,
  FieldTextRequired,
} from '../../../form';
import { SlideFormProps } from './types';
import SlidePreview from './SlidePreview';
import { iriToId } from '../../../util';
import { InfoOutlined } from '@mui/icons-material';

export default function TextImageSlideForm({
  slide,
  onUpdateField,
  hasQuestionInOtherSlide,
}: SlideFormProps) {
  return (
    <Stack direction="row" gap={3}>
      <Box sx={{ flex: 1 }}>
        <Stack gap={2}>
          <FieldTextRequired
            field={{
              name: 'title',
              label: 'Title',
              value: slide.templateData.title,
              handleValueChange: (value) => onUpdateField('title', value),
            }}
          />
          <FieldTextRequired
            field={{
              name: 'markdown',
              label: 'Description',
              value: slide.templateData.markdown,
              handleValueChange: (value) => onUpdateField('markdown', value),
            }}
            multiline
            rows={4}
            helperText="max. 300 characters"
            slotProps={{
              htmlInput: {
                maxLength: 300,
              },
            }}
          />
          <FieldMediaUpload
            field={{
              name: 'image_id',
              label: 'Image',
              value: slide.templateData.image_id,
              handleValueChange: (value) =>
                onUpdateField('image_id', value ? iriToId(value) : null),
            }}
            required
            acceptedTypes={['IMAGE_TYPE']}
            existingImageId={slide.templateData.image_id}
            helperText="2:3 aspect ratio"
            aspectRatio="2/3"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <FieldSwitch
              field={{
                name: 'add_question',
                label: 'Add a question to this slide?',
                value: slide.templateData.add_question,
                handleValueChange: (value) =>
                  !hasQuestionInOtherSlide &&
                  onUpdateField('add_question', value),
              }}
              disabled={hasQuestionInOtherSlide}
            />
            {hasQuestionInOtherSlide && (
              <Alert
                severity="info"
                icon={<InfoOutlined />}
                sx={{
                  backgroundColor: 'transparent',
                  padding: 0,
                  '& .MuiAlert-icon': {
                    color: 'info.main',
                    padding: 0,
                    marginRight: '8px',
                  },
                  '& .MuiAlert-message': {
                    fontSize: '14px',
                    color: 'info.main',
                    padding: 0,
                  },
                }}
              >
                Only one question per story - You've already added a question on
                another slide.
              </Alert>
            )}
          </Box>
          {slide.templateData.add_question && !hasQuestionInOtherSlide && (
            <FieldText
              field={{
                name: 'question',
                label: 'Question',
                value: slide.templateData.question,
                handleValueChange: (value) => onUpdateField('question', value),
              }}
              helperText="max. 80 characters"
              slotProps={{
                htmlInput: {
                  maxLength: 80,
                },
              }}
            />
          )}
        </Stack>
      </Box>

      <SlidePreview
        title={slide.templateData.title}
        description={slide.templateData.markdown}
        image={slide.templateData.image_id}
        type="text-image"
        question={
          slide.templateData.add_question ? slide.templateData.question : null
        }
      />
    </Stack>
  );
}
